// src/lib/featureCapture.js — which registry features the ADD/EDIT-PROPERTY form offers, and how
// an answer to each leaves the form. Pure, so node --test can hold it.
//
// Owner 2026-09-16: "manual property creation has all the fields that are known to our system,
// and each is pickable." The form offered ~20 hand-typed chips while the `feature` registry holds
// ~590 codes, so everything an agent learned on the phone beyond those 20 had nowhere to go.
//
// ⚠️ THIS IS NOT THE SEARCH PANEL'S LIST, AND MUST NOT BE MADE TO BE. searchVocab.featureGroupsFrom
// hides a code nobody has recorded yet (a filter must never offer a confident zero). A CAPTURE form
// is the opposite: an unrecorded code is exactly the one that needs a first answer. What the two
// DO share is the kind gate — capabilityFilter (0759) is called, never restated.
//
// What is left out, and why — each is a place where the answer would be dropped or mis-stored:
//   • inactive / derived codes            — retired, or computed from other facts
//   • property_type                       — the kind picker IS that answer
//   • tenant_terms (policy mode)          — needs a stance; the restrictions panel records it
//   • location                            — core_ingest NON_FEATURE_CATEGORIES rejects it
//   • listing_quality                     — describes a PORTAL ad's photos; a manual entry has none
//   • condition / renovation / frame / apartment_class — one-of ladders the form already asks
//   • subcategory heating_object          — Lynceus's located objects, not a question (0590)
//   • status / financial / communication / motivation / cooperation — about the LISTING, the deal or
//     the owner contact, not the property (owner 2026-09-16: "keep anything not property related
//     from here"). They have their own surfaces: deal terms, cooperation, call outcomes.
import { capabilityFilter } from './searchVocab.js'

const EXCLUDED_CATEGORIES = new Set(['property_type', 'tenant_terms', 'location', 'listing_quality',
  'condition', 'renovation', 'frame', 'apartment_class',
  'status', 'financial', 'communication', 'motivation', 'cooperation'])

// Categories core_ingest routes AWAY from property_feature to context signals (location_character,
// neighbourhood opinions). That writer takes the CODE and ignores the polarity, so a "no" would be
// stored as a "yes". They are offered as a single yes.
const YES_ONLY_CATEGORIES = new Set(['location_character'])

// Same order the search panel reads in, then the categories only capture shows.
const GROUP_ORDER = ['building', 'planning', 'outdoor', 'kitchen', 'bathroom', 'furniture', 'utilities',
  'security', 'garage', 'orientation_view', 'orientation', 'land', 'commercial', 'amenity', 'defect',
  'environment', 'neighborhood', 'location_character']

export const MAX_CHIP_ANSWERS = 64   // core_prepare refuses more in one save

export function answerModeOf(f) {
  if (!f) return null
  if (YES_ONLY_CATEGORIES.has(f.category)) return 'yes_only'
  if (f.category === 'neighborhood' && f.subcategory === 'opinion') return 'yes_only'
  return 'tri'
}

/**
 * Groups of feature rows the form offers for `kind`: [[category, [row…]], …].
 * `exclude` = codes another control on the form already asks (lift, heating, type chips…), so a
 * fact never has two controls that could disagree.
 */
export function captureFeatureGroups(vocab, kind, kindCaps, exclude = new Set()) {
  const rows = Array.isArray(vocab) ? vocab : []
  const allow = capabilityFilter(kind ? [kind] : [], kindCaps)
  const seen = new Set()
  const byGroup = new Map()
  for (const f of rows) {
    const code = f && f.code
    if (!code || seen.has(code)) continue
    seen.add(code)
    if (f.active === false || f.derived) continue
    if (f.mode === 'policy' || EXCLUDED_CATEGORIES.has(f.category)) continue
    if (f.subcategory === 'heating_object') continue
    if (exclude.has(code)) continue
    if (!allow(code, f.category)) continue
    const g = f.category || 'other'
    if (!byGroup.has(g)) byGroup.set(g, [])
    byGroup.get(g).push(f)
  }
  const out = [...byGroup.entries()].map(([g, fs]) => [g, fs.sort((a, b) =>
    String(a.subcategory || '').localeCompare(String(b.subcategory || '')) || a.code.localeCompare(b.code))])
  out.sort((a, b) => {
    const ia = GROUP_ORDER.indexOf(a[0]), ib = GROUP_ORDER.indexOf(b[0])
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a[0].localeCompare(b[0])
  })
  return out
}

/** Case-insensitive match on every label and alias, so an agent can type the word they heard. */
export function featureMatches(f, q) {
  const s = String(q || '').trim().toLowerCase()
  if (!s) return true
  return [f.code, f.name_ka, f.name_en, f.name_ru, ...(Array.isArray(f.aliases) ? f.aliases : [])]
    .some(v => v && String(v).toLowerCase().includes(s))
}

/**
 * Prefill from the card's merged feature rows: {code: 'true'|'false'}. Same rule as the lift/parking
 * prefill — a scrape-only negative is an unticked portal box, i.e. unknown, never an absence.
 */
export function featureAnswersFromRows(rows) {
  const out = {}
  const list = Array.isArray(rows) ? rows : []
  for (const r of list) {
    if (!r || !r.feature_key) continue
    if (r.present === true) out[r.feature_key] = 'true'
  }
  for (const r of list) {
    if (!r || !r.feature_key || out[r.feature_key] === 'true') continue
    if (r.present === false && !['scrape', 'scrape_reshare'].includes(r.source_kind)) out[r.feature_key] = 'false'
  }
  return out
}

/**
 * The answers that CHANGED since the prefill, as chip_states. Clearing an answer sends nothing —
 * the form has no way to retract a feature, and sending the opposite would assert what the agent
 * did not say. A yes-only code never leaves as false.
 */
export function changedFeatureStates(base, cur, modeOf = () => 'tri') {
  const out = {}
  const b = base || {}
  for (const [code, v] of Object.entries(cur || {})) {
    if (v !== 'true' && v !== 'false') continue
    if (v === b[code]) continue
    if (v === 'false' && modeOf(code) === 'yes_only') continue
    out[code] = v === 'true'
  }
  return Object.keys(out).length ? out : undefined
}

// ── Taking an answer back (2026-09-16, "fix all of them") ───────────────────────────────────────
// Every feature answer the agent CLEARED since the prefill: an answered feature now blank, a lift or
// parking answer reset to blank, a heating type deselected, the state/class rung removed or changed,
// a type chip untoggled. The server removes only the ones this agent wrote (retract_own_features,
// 1222) and names the rest back. `building` = false on land, whose building controls are hidden, so a
// hidden answer is never taken back by accident.
export function featureRetractCodes(base, cur, { building = true } = {}) {
  const b = base || {}, c = cur || {}
  const out = new Set()
  const answered = v => v === 'true' || v === 'false'
  for (const [code, v] of Object.entries(b.featStates || {})) {
    if (answered(v) && !answered((c.featStates || {})[code])) out.add(code)
  }
  if (building) {
    for (const k of ['elevator', 'parking']) {
      if (answered((b.featAns || {})[k]) && !answered((c.featAns || {})[k])) out.add(k)
    }
    for (const code of (b.featAns || {}).heat || []) if (!((c.featAns || {}).heat || []).includes(code)) out.add(code)
    if (b.classCode && b.classCode !== c.classCode) out.add(b.classCode)
    // the condition block's three answers (building / condition / class, 2026-09-24): a changed or cleared one is taken back
    for (const [k, code] of Object.entries(b.rungs || {})) if (code && code !== (c.rungs || {})[k]) out.add(code)
  }
  for (const code of b.typeTags || []) if (!(c.typeTags || []).includes(code)) out.add(code)
  return [...out]
}

// What a save response says did NOT land, as [key, detail] pairs for the form to show. Routes that
// are successful by design (context signals, listing state, rerouted chips) are not warnings.
const PROBLEM_KEYS = ['unknownTags', 'unknownFeatures', 'implausible', 'invalid', 'derivedSkipped', 'nonFeature',
  'listingStateSkipped', 'needsPolicyStance', 'claimFailed', 'policyWriteFailed', 'giraoTermsFailed',
  'petTermsFailed', 'subtypeUnknown', 'cadastralConflict']
export function saveWarningsFrom(r) {
  if (!r || typeof r !== 'object') return []
  const out = []
  const list = v => (Array.isArray(v) ? v.map(x => (typeof x === 'string' ? x : JSON.stringify(x))).join(', ') : String(v))
  for (const k of PROBLEM_KEYS) {
    const v = r.unresolved && r.unresolved[k]
    if (v != null && !(Array.isArray(v) && !v.length)) out.push([k, list(v)])
  }
  if (Array.isArray(r.retract_refused) && r.retract_refused.length) out.push(['retract_refused', list(r.retract_refused)])
  if (Array.isArray(r.features_not_yours) && r.features_not_yours.length) out.push(['features_not_yours', list(r.features_not_yours)])
  if (r.features_retract_error) out.push(['features_retract_error', String(r.features_retract_error)])
  return out
}

// Plot area: always STORED in square metres (land_area_sqm); land_area_unit records what the owner said.
export const LAND_UNITS = { sqm: 1, ari: 100, ha: 10000 }
export function landAreaToSqm(value, unit) {
  if (value === '' || value == null || !isFinite(+value)) return ''
  const f = LAND_UNITS[unit] || 1
  return String(Math.round(+value * f * 100) / 100)
}
export function sqmToLandUnit(sqm, unit) {
  if (sqm === '' || sqm == null || !isFinite(+sqm)) return ''
  const f = LAND_UNITS[unit] || 1
  return String(Math.round((+sqm / f) * 10000) / 10000)
}

// The building year rule lives in buildingFacts.js (shared with the valuation tab); re-exported here.
export { yearBuiltValid } from './buildingFacts.js'

// ── "All features" views (redesign stage 2, from the mockup's tabs) ─────────────────────────────
// Codes where the recorded answers DISAGREE: at least one "has it" and one "does not" from sources
// that can state a negative. A scrape-only negative is an unticked portal box — unknown, not a no —
// so it never makes a disagreement (the same rule as featureAnswersFromRows).
export function disagreeingFeatureCodes(rows) {
  const yes = new Set(), no = new Set()
  for (const r of Array.isArray(rows) ? rows : []) {
    if (!r || !r.feature_key) continue
    if (r.present === true) yes.add(r.feature_key)
    else if (r.present === false && !['scrape', 'scrape_reshare'].includes(r.source_kind)) no.add(r.feature_key)
  }
  return new Set([...yes].filter(c => no.has(c)))
}

// The groups a view shows. 'unanswered' keeps every row, unanswered rows first (answered ones sink,
// never disappear); 'ai' and 'disagreeing' keep only those rows, dropping empty groups.
export function featureView(groups, answers, mode = 'unanswered', { aiCodes = null, disagree = null } = {}) {
  const a = answers || {}
  const answered = code => a[code] === 'true' || a[code] === 'false'
  const keep = mode === 'ai' ? (f => !!aiCodes && aiCodes.has(f.code))
    : mode === 'disagreeing' ? (f => !!disagree && disagree.has(f.code))
    : null
  const out = []
  for (const [g, rows] of groups || []) {
    let list = keep ? rows.filter(keep) : [...rows]
    if (!keep) list = [...list.filter(f => !answered(f.code)), ...list.filter(f => answered(f.code))]
    if (list.length) out.push([g, list])
  }
  return out
}

// Feature answers a save did NOT write: codes ingest could not store, and — when the removal call itself
// failed — every code that was being taken back. They keep their previous baseline so they stay retryable.
export function failedFeatureCodes(r, retractCodes = []) {
  const u = (r && r.unresolved) || {}
  const out = new Set([...(u.unknownFeatures || []), ...(u.unknownTags || [])].map(String))
  if (r && r.features_retract_error) for (const c of retractCodes) out.add(c)
  return out
}
